/**
 * Video insights: proposals stored on the video attachment (Video Insights
 * MVP). One job per mode, 2-11 minutes end to end. Not polled: every state
 * write is a CAS `updateMany` on the video's artifact row, which the audit
 * extension's bulk hook broadcasts on `/sync`, and `invalidateJobStateQueries`
 * (`lib/jobStateInvalidation.ts`) refetches this query when a change names the
 * video. A write the socket missed (down, or before the room was first joined)
 * is caught by the room-join refetch of any query still showing `queued`.
 */
import type { VideoInsightRequest, VideoInsightsMetadata } from '@bower/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { videoInsightsKey } from '../../lib/jobStateInvalidation';
import { getVideoInsights, requestVideoInsight } from '../../services/api/attachments.api';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { apiService } from '../../services/api.service';
import { safeAnalytics } from '../../utils/analytics';

export { videoInsightsKey };

/**
 * Stored video-insight proposals for one video attachment.
 *
 * 404 handling: the route is flag-gated and 404s (never 403 — the surface
 * must not be disclosed) when `VIDEO_INSIGHTS_ENABLED` is off. The panel that
 * mounts this hook is itself flag-gated on the frontend, so a 404 here can
 * only happen on a flag-propagation race, not a real user-facing failure.
 * Resolving it as "no insights yet" (rather than throwing into the query's
 * error state) avoids flashing a spurious error banner for what is really
 * "not entitled right now" — every OTHER failure (network, 5xx, a genuinely
 * missing attachment) still throws, per rule 12 (fail loud on real failures).
 */
export function useVideoInsights(videoArtifactId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: videoInsightsKey(videoArtifactId ?? ''),
    queryFn: async (): Promise<VideoInsightsMetadata> => {
      const response = await getVideoInsights(apiService, videoArtifactId ?? '');
      if (!response.success) {
        if (response.error?.status === 404) return {};
        throw new Error(response.error?.message ?? 'Failed to load video insights');
      }
      return response.data?.insights ?? {};
    },
    enabled: enabled && !!videoArtifactId,
    // The resource only changes when a 2-11 minute job lands, and that write's
    // /sync event refetches it, so a mount or refocus need not re-GET.
    staleTime: 5_000,
  });
}

/**
 * Where the user started the request from. Every call site emits the same
 * `Video Insight Request Initiated` event, and `{ mode, has_context }` alone
 * cannot tell the attachment kebab apart from the protocol page — so "is the
 * new placement used?" was unanswerable in PostHog.
 *
 * A closed union, and REQUIRED rather than defaulted: a `'unknown'` fallback is
 * how a fourth call site ships unlabelled, whereas a required parameter fails
 * type-check until someone names the surface. Fixed identifiers only — never a
 * title, filename or step, which the video-insight UGC guard
 * (`constants/__tests__/analytics.video-insight-ugc.test.ts`) enforces.
 *
 * `attachment_kebab` reuses the literal `OcrTriggeredProperties.trigger_source`
 * already uses for the same menu, so the two AI features stay comparable.
 */
export type VideoInsightTriggerSource =
  | 'attachment_kebab'
  | 'protocol_video_empty_state'
  | 'protocol_insights_panel';

/** Request one video-insight mode. Enqueues (202); the query above picks up the result. */
export function useRequestVideoInsight(
  videoArtifactId: string | undefined,
  triggerSource: VideoInsightTriggerSource,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: VideoInsightRequest) => {
      if (!videoArtifactId) throw new Error('Video artifact id is required');
      const response = await requestVideoInsight(apiService, videoArtifactId, body);
      // `unwrapApiResponse`, not a bare `new Error(message)`: the backend
      // answers a duplicate request with 409 / ALREADY_IN_PROGRESS, and only
      // an `ApiResponseError` carries the status + code a caller needs to say
      // "already running" rather than "it failed".
      return unwrapApiResponse(response, 'Failed to request video insight');
    },
    onSuccess: (_data, body) => {
      safeAnalytics.track(ANALYTICS_EVENTS.VIDEO_INSIGHT_REQUEST_INITIATED, {
        mode: body.mode,
        has_context: !!body.contextArtifactId,
        trigger_source: triggerSource,
      });
      if (!videoArtifactId) return undefined;
      // RETURNED, not fire-and-forget: TanStack awaits a promise returned from
      // onSuccess before the mutation settles, so `isPending` stays true until
      // the refetch has committed the new `queued` row. Without that, the
      // button re-enables in the window between the POST resolving and the
      // GET landing.
      //
      // The controller now ALSO refuses an in-flight duplicate with a 409, so
      // this is no longer the only thing standing between a double click and a
      // second 2-11 minute Vertex read. Keep it anyway: the 409 costs a round
      // trip and surfaces as a toast, while this keeps the button honest so
      // the user never asks twice in the first place. Belt and braces, with
      // the server holding the braces.
      return queryClient.invalidateQueries({ queryKey: videoInsightsKey(videoArtifactId) });
    },
  });
}
